import { motion } from "framer-motion";
import { ArrowUpDown, ArrowUp, ArrowDown, Eye, Trash2, Mail, Smartphone, UploadCloud, Camera, KeyRound, PenLine, Landmark, Inbox, ArrowRightLeft, Printer } from "lucide-react";
import { getCategory } from "../lib/categories";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Button } from "../components/ui/button";
import { tr } from "../lib/i18n";

const sourceIcon = { upload: UploadCloud, email: Mail, sms: Smartphone, text: Mail, camera: Camera, import: KeyRound, builder: PenLine, bank: Landmark, plaid: Landmark, gmail: Inbox, migration: ArrowRightLeft, fax: Printer };

const SortHead = ({ label, field, sort, setSort, className }) => {
  const active = sort.field === field;
  const Icon = !active ? ArrowUpDown : sort.dir === "asc" ? ArrowUp : ArrowDown;
  return (
    <TableHead className={className}>
      <button
        data-testid={`sort-${field}`}
        onClick={() =>
          setSort({ field, dir: active && sort.dir === "asc" ? "desc" : "asc" })
        }
        className={`inline-flex items-center gap-1.5 hover:text-foreground transition-colors ${
          active ? "text-foreground" : ""
        }`}
      >
        {label}
        <Icon className="h-3.5 w-3.5" />
      </button>
    </TableHead>
  );
};

const firstField = (doc) => {
  const entries = Object.entries(doc.extracted_fields || {});
  return entries.length ? `${entries[0][0]}: ${entries[0][1]}` : "—";
};

export const DocumentTable = ({ documents, sort, setSort, onInspect, onDelete }) => {
  return (
    <div data-testid="document-table" className="rounded-2xl border border-border bg-card overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <SortHead label={tr("Document")} field="title" sort={sort} setSort={setSort} />
            <SortHead label={tr("Category")} field="category" sort={sort} setSort={setSort} className="hidden sm:table-cell" />
            <TableHead className="hidden lg:table-cell">{tr("Extracted highlight")}</TableHead>
            <TableHead className="hidden md:table-cell">{tr("Source")}</TableHead>
            <SortHead label={tr("Date")} field="uploaded_at" sort={sort} setSort={setSort} />
            <SortHead label={tr("Confidence")} field="confidence" sort={sort} setSort={setSort} className="hidden sm:table-cell text-right" />
            <TableHead className="text-right">{tr("Actions")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {documents.map((doc) => {
            const cat = getCategory(doc.category);
            const CatIcon = cat.icon;
            const SourceIcon = sourceIcon[doc.source] || UploadCloud;
            const conf = Math.round((doc.confidence || 0) * 100);
            return (
              <motion.tr
                key={doc.id}
                data-testid="table-row-item"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                onClick={() => onInspect(doc)}
                className="cursor-pointer border-b border-border last:border-0 hover:bg-accent/50 transition-colors"
              >
                <TableCell className="font-medium max-w-[240px]">
                  <div className="flex items-center gap-2.5">
                    <div className={`h-8 w-8 rounded-lg grid place-items-center shrink-0 ${cat.tint}`}>
                      <CatIcon className="h-4 w-4" style={{ color: cat.iconColor }} />
                    </div>
                    <span className="truncate">{doc.title}</span>
                  </div>
                </TableCell>
                <TableCell className="hidden sm:table-cell">
                  <Badge className={`${cat.badge} border-0 capitalize`}>{tr(cat.label)}</Badge>
                </TableCell>
                <TableCell className="hidden lg:table-cell text-sm font-mono text-muted-foreground max-w-[220px] truncate">
                  {firstField(doc)}
                </TableCell>
                <TableCell className="hidden md:table-cell">
                  <span className="inline-flex items-center gap-1.5 text-sm capitalize text-muted-foreground">
                    <SourceIcon className="h-3.5 w-3.5" /> {doc.source}
                  </span>
                </TableCell>
                <TableCell className="text-sm font-mono whitespace-nowrap">
                  {new Date(doc.uploaded_at).toLocaleDateString(undefined, {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  })}
                </TableCell>
                <TableCell className="hidden sm:table-cell text-right font-mono text-sm">
                  {conf}%
                </TableCell>
                <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-end gap-1">
                    <Button size="icon" variant="ghost" onClick={() => onInspect(doc)}>
                      <Eye className="h-4 w-4" />
                    </Button>
                    {onDelete && (
                      <Button
                        size="icon"
                        variant="ghost"
                        className="text-destructive hover:text-destructive"
                        onClick={() => onDelete(doc)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </motion.tr>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};
